/**
 * Utility function to extract detailed error messages from API responses
 * Handles various error response structures and formats user-friendly messages
 */

/**
 * Extract error message from error object
 * @param {Error} error - The error object from API call
 * @returns {string} - User-friendly error message with details
 */
export function getErrorMessage(error) {
  if (!error) {
    return "An unknown error occurred";
  }

  // If error has a response (axios error)
  if (error.response) {
    const responseData = error.response.data;
    
    // Handle different response structures
    if (typeof responseData === 'string') {
      return responseData;
    }

    if (responseData) {
      // Check for message field
      if (responseData.message) {
        return responseData.message;
      }

      // Check for error field
      if (responseData.error) {
        if (typeof responseData.error === 'string') {
          return responseData.error;
        }
        if (responseData.error.message) {
          return responseData.error.message;
        }
      }

      // Check for errors field (array of errors)
      if (responseData.errors && Array.isArray(responseData.errors)) {
        const errorMessages = responseData.errors.map(err => {
          if (typeof err === 'string') return err;
          if (err.message) return err.message;
          if (err.field && err.defaultMessage) {
            return `${err.field}: ${err.defaultMessage}`;
          }
          return JSON.stringify(err);
        });
        return errorMessages.join(', ');
      }

      // Check for details field
      if (responseData.details) {
        if (typeof responseData.details === 'string') {
          return responseData.details;
        }
        if (Array.isArray(responseData.details)) {
          return responseData.details.join(', ');
        }
      }

      // Check for validation errors structure
      if (responseData.validationErrors) {
        if (Array.isArray(responseData.validationErrors)) {
          return responseData.validationErrors.map(err => 
            `${err.field || 'Field'}: ${err.message || err.defaultMessage || 'Invalid value'}`
          ).join(', ');
        }
      }

      // If none of the above, try to stringify the response data
      try {
        const jsonString = JSON.stringify(responseData);
        if (jsonString && jsonString !== '{}') {
          return jsonString;
        }
      } catch (e) {
        // Ignore JSON stringify errors
      }
    }

    // Handle HTTP status codes
    switch (error.response.status) {
      case 400:
        return "Bad request. Please check your input data.";
      case 401:
        return "Authentication failed. Please login again.";
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 409:
        return "Conflict: The data already exists or conflicts with existing data.";
      case 422:
        return "Validation error. Please check your input data.";
      case 500:
        return "Server error. Please try again later.";
      default:
        return `Request failed with status ${error.response.status}`;
    }
  }

  // If error is a network error (no response)
  if (error.request) {
    return "Network error. Please check your internet connection.";
  }

  // If error has a message property
  if (error.message) {
    return error.message;
  }

  // Fallback
  return "An unexpected error occurred. Please try again.";
}

/**
 * Show error toast with detailed message
 * @param {Function} toast - Toast function from react-toastify
 * @param {Error} error - The error object
 * @param {string} fallbackMessage - Fallback message if error extraction fails
 */
export function showErrorToast(toast, error, fallbackMessage = "An error occurred") {
  const errorMessage = getErrorMessage(error);
  toast.error(errorMessage || fallbackMessage);
}

/**
 * Extract field-specific errors from error response
 * @param {Error} error - The error object from API call
 * @returns {Object} - Object mapping field names to error messages
 */
export function getFieldErrors(error) {
  if (!error || !error.response || !error.response.data) {
    return {};
  }

  const responseData = error.response.data;
  const fieldErrors = {};

  // Check for errors array with field information
  if (responseData.errors && Array.isArray(responseData.errors)) {
    responseData.errors.forEach(err => {
      if (err.field) {
        fieldErrors[err.field] = err.message || err.defaultMessage || 'Invalid value';
      }
    });
  }

  // Check for validationErrors object
  if (responseData.validationErrors) {
    if (Array.isArray(responseData.validationErrors)) {
      responseData.validationErrors.forEach(err => {
        if (err.field) {
          fieldErrors[err.field] = err.message || err.defaultMessage || 'Invalid value';
        }
      });
    } else if (typeof responseData.validationErrors === 'object') {
      Object.entries(responseData.validationErrors).forEach(([field, message]) => {
        fieldErrors[field] = Array.isArray(message) ? message.join(', ') : message;
      });
    }
  }

  // Check for field-specific errors in the response
  if (responseData.fieldErrors) {
    Object.entries(responseData.fieldErrors).forEach(([field, message]) => {
      fieldErrors[field] = Array.isArray(message) ? message.join(', ') : message;
    });
  }

  return fieldErrors;
}

/**
 * Map API field names to form field names
 * @param {string} apiField - Field name from API response
 * @returns {string} - Corresponding form field name
 */
export function mapApiFieldToFormField(apiField) {
  const fieldMap = {
    'email': 'email',
    'contactNumber': 'mobile',
    'phoneNumber': 'mobile',
    'phone': 'mobile',
    'name': 'name',
    'customerName': 'name',
    'address': 'address',
    'siteLocation': 'siteLocation',
    'vehicleNumber': 'vehicleNumber',
    'vehicleNo': 'vehicleNo',
    'transporter': 'transporter',
    'driverName': 'driverDetails',
    'driverNumber': 'driverNumber',
    'challanNumber': 'challanNumber',
    'date': 'date',
    'purchaseOrderNo': 'poNo',
    'deposite': 'balance',
    'depositAmount': 'depositAmount',
  };
  
  return fieldMap[apiField] || apiField;
}

/**
 * Show validation error toast with field-specific details
 * @param {Function} toast - Toast function from react-toastify
 * @param {Object} validationErrors - Validation errors object
 */
export function showValidationErrorToast(toast, validationErrors) {
  if (!validationErrors || Object.keys(validationErrors).length === 0) {
    toast.error("Validation failed. Please check your input.");
    return;
  }

  const errorMessages = Object.entries(validationErrors).map(([field, message]) => {
    if (Array.isArray(message)) {
      return `${field}: ${message.join(', ')}`;
    }
    return `${field}: ${message}`;
  });

  toast.error(errorMessages.join(', '));
}
